"use strict";
import { useMemo } from "react";
import { useIsSmartContractAddress } from "features/address/useIsSmartContractAddress";
import { useEnabledChains } from "features/chains/hooks/useEnabledChains";
import { ENS_SUFFIX } from "features/ens/constants";
import { useENS } from "features/ens/useENS";
import { SearchResultType } from "features/search/SearchResult";
import { UNITAG_SUFFIX } from "features/unitags/constants";
import { useUnitagByAddress, useUnitagByName } from "features/unitags/hooks";
import { getValidAddress } from "utils/addresses";
export function useWalletSearchResults(query, selectedChain) {
  const { defaultChainId } = useEnabledChains();
  const validAddress = useMemo(() => getValidAddress(query, true, false) ?? void 0, [query]);
  const querySkippedIfValidAddress = validAddress ? null : query;
  const {
    address: dotEthAddress,
    name: dotEthName,
    loading: dotEthLoading
  } = useENS({ nameOrAddress: querySkippedIfValidAddress, autocompleteDomain: true });
  const {
    address: ensAddress,
    name: ensName,
    loading: ensLoading
  } = useENS({ nameOrAddress: querySkippedIfValidAddress, autocompleteDomain: false });
  const { unitag: unitagByName, loading: unitagLoading } = useUnitagByName(query);
  const { unitag: unitagByAddress, loading: unitagByAddressLoading } = useUnitagByAddress(
    validAddress ?? ensAddress ?? dotEthAddress ?? void 0
  );
  const { isSmartContractAddress, loading: loadingIsSmartContractAddress } = useIsSmartContractAddress(
    validAddress,
    selectedChain ?? defaultChainId
  );
  const hasENSResult = dotEthName && dotEthAddress;
  const hasEOAResult = validAddress && !isSmartContractAddress;
  const exactENSMatch = dotEthName?.toLowerCase() === query.toLowerCase() && query.includes(ENS_SUFFIX);
  const results = [];
  if (unitagByName?.address?.address && unitagByName?.username) {
    results.push({
      type: SearchResultType.Unitag,
      address: unitagByName.address.address,
      unitag: unitagByName.username
    });
  }
  const showUnitagOverEns = !exactENSMatch || dotEthName.endsWith(UNITAG_SUFFIX);
  const addressMatch = unitagByAddress?.address === validAddress;
  const nameMatch = unitagByAddress?.username && query.startsWith(unitagByAddress.username);
  const addressOrNameMatch = addressMatch || nameMatch && showUnitagOverEns;
  const showUnitagByAddress = !unitagByName?.address?.address && unitagByAddress?.address && unitagByAddress?.username && addressOrNameMatch;
  if (showUnitagByAddress) {
    results.push({
      type: SearchResultType.Unitag,
      // Already checked that these aren't undefined but linter doesn't recognize it
      address: unitagByAddress.address ?? "",
      unitag: unitagByAddress.username ?? ""
    });
  }
  if (!validAddress && ensAddress && ensName && !showUnitagByAddress) {
    results.push({
      type: SearchResultType.ENSAddress,
      address: ensAddress,
      ensName,
      isRawName: !ensName.endsWith(ENS_SUFFIX)
      // Ensure raw name is used for subdomains only
    });
  }
  const differentFromUnitagByAddress = dotEthAddress !== unitagByAddress?.address || !showUnitagByAddress;
  if (!validAddress && hasENSResult && dotEthAddress !== unitagByName?.address?.address && differentFromUnitagByAddress && dotEthAddress !== ensAddress) {
    results.push({
      type: SearchResultType.ENSAddress,
      address: dotEthAddress,
      ensName: dotEthName
    });
  }
  if (hasEOAResult && !showUnitagByAddress) {
    results.push({
      type: SearchResultType.WalletByAddress,
      address: validAddress
    });
  }
  const walletsLoading = dotEthLoading || ensLoading || loadingIsSmartContractAddress || unitagLoading || unitagByAddressLoading;
  return {
    loading: walletsLoading,
    wallets: results,
    exactENSMatch,
    exactUnitagMatch: !!(unitagByName || unitagByAddress)
  };
}
